<template>
  <div class="articles-form">
    <page-header :header="header">
      <div
        v-if="recipients !== null"
        class="align-self-center mr-3 mb-1"
      >
        {{ recipients }} recipients
      </div>
      <presence-inline />
    </page-header>

    <stepper
      :steps="steps"
      :active-step-index="activeStepIndex"
      @change-step="navigateToStep"
    />

    <keep-alive>
      <component
        class="article-create-main"
        :is="steps[activeStepIndex].component"
        :article="article"
        :highlight-fields="highlightFields"
        :errors="errorData"
        :analysts="analysts"
        :content-types="contentTypes"
        :layouts="layouts"
        :can-publish="canPublish"
        :is-editor="isEditor"
        @send-test="sendTest"
        @update="applyArticleData"
        @add-tag="addTag"
        @change-step="navigateToStep"
      />
    </keep-alive>

    <div class="d-flex article-create-navigation" style="justify-content: flex-end;">
      <button
        v-if="article.id || article.article_draft_id"
        class="btn btn-outline-danger mr-auto"
        @click="deleteArticle"
      >
        <span class="d-none d-md-flex">
          Delete
        </span>
        <icon
          icon="delete"
          class="d-flex d-md-none"
        />
      </button>
      <a
        href="javascript:;"
        class="mx-1 px-3 mt-2 mb-2 border-right"
        v-if="!article.id || isRepublish"
        @click="saveDraft"
      >
        <span class="d-none d-md-flex">
          Save
        </span>
        <icon
          icon="save"
          class="d-flex d-md-none"
        />
      </a>
      <a
        href="javascript:;"
        class="mx-1 px-2 mt-2 mb-2 border-right"
        v-else-if="activeStepIndex !== steps.length - 1"
        @click="saveArticle"
      >
        <span class="d-none d-md-flex">
          Update
        </span>
        <icon
          icon="save"
          class="d-flex d-md-none"
        />
      </a>

      <a
        href="javascript:;"
        v-if="activeStepIndex !== 0"
        class="mx-1 px-2 mt-2 mb-2"
        @click="goBack"
      >
        Back
      </a>
      <button
        v-if="activeStepIndex !== steps.length - 1"
        class="btn btn-primary ml-3"
        @click="goNext"
      >
        Next
      </button>
      <button
        class="btn btn-outline-primary ml-3"
        v-if="!article.id && activeStepIndex === steps.length - 1"
        @click="sendTest"
      >
        <span class="d-none d-md-flex">
          Send Test
        </span>
        <icon
          icon="save"
          class="d-flex d-md-none"
        />
      </button>
      <button
        v-if="canPublish && activeStepIndex === steps.length - 1 && !article.id && !isRepublish"
        class="btn btn-outline-primary ml-3"
        @click="schedule"
      >
        Schedule
      </button>
      <button
        v-if="canPublish && activeStepIndex === steps.length - 1 && !article.id && !isRepublish"
        class="btn btn-primary ml-3"
        :class="{ loading: isPublishing }"
        @click="() => needsAskBeforePublished ? askToPublish() : publishArticle()"
      >
        Publish
      </button>
      <button
        v-if="!isDraft && canPublish && activeStepIndex === steps.length - 1 && !isRepublish"
        class="btn btn-primary ml-3"
        @click="saveArticle"
      >
        Save
      </button>
      <button
          v-if='canPublish && activeStepIndex === steps.length - 1 && article.id && isRepublish'
          class='btn btn-primary ml-3'
          @click='askToRepublish()'
      >
        Republish and send alert
      </button>

      <button
        v-if="!canPublish && activeStepIndex === steps.length - 1"
        class="btn btn-primary ml-3"
        @click="submitToEditorial"
      >
        Submit for Review
      </button>
    </div>
    <confirm
        modal='confirm-republish'
        @confirmed='republishAndSendAlert'
    >
      Are you sure you want to republish this article?
    </confirm>

    <confirm
      modal="confirm-publish"
      @confirmed="publishArticle"
    >
      Are you sure you want to publish without a PDF?
    </confirm>
    <modal
      name="schedule"
      height="393"
    >
      <div class="p-3" style="color: #242424;">

        <modal-title>
          Schedule article publication
        </modal-title>
        <div class="mt-1">
          <div class="mb-3">
            Publication date &amp; time (London Time)
          </div>
          <div class="d-flex">
            <flat-pickr
              class="has-suffix"
              v-model="publishedAt"
              @input="updatePublishedAt"
              :config="{enableTime: true}"
              placeholder="Select date and time"
            />
            <div class="input-suffix">
              <icon icon="date" />
            </div>
          </div>
          <div class="mt-3">
            <table class="table table-sm" style="font-size: 12px">
              <thead>
                <tr>
                  <th>New York</th>
                  <th>London</th>
                  <th>Dubai</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ newYork }}</td>
                  <td>{{ london }}</td>
                  <td>{{ dubai }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="border-top pt-4 mt-4 d-flex">
            <button class="btn btn-block btn-outline-danger" @click="cancelSchedule">
              Cancel
            </button>
            <button
              class="btn btn-block btn-primary ml-3 mt-0"
              @click="publishArticle"
            >
              Schedule Publication
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import gateway from '@/gateway'
import { infoNotification, successNotification, errorNotification, warningNotification } from '@/notifications'
import Stepper from '@/components/stepper/Stepper'
import TypeStep from './TypeStep'
import ArticleContentStep from './ArticleContentStep'
import PublishStep from './PublishStep'
import Confirm from '@/components/Confirm'
import debounce from 'lodash.debounce'
import PresenceInline from '@/components/PresenceInline'
import ModalTitle from '@/components/ModalTitle'
import { getOnce } from '@/helpers'
import moment from 'moment-timezone'

export default {
  name: 'ArticleForm',

  components: {
    Stepper,
    Confirm,
    PresenceInline,
    ModalTitle,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    isRepublish: {
      type: Boolean,
      default: () => false
    },
    header: {
      type: String,
      default: () => 'Create new article',
    },
  },

  data () {
    return {
      article: {...this.data},
      recipients: null,
      activeStepIndex: 0,
      highlightFields: false,
      user: this.$login.getUser(),
      channelId: this.$login.channelScopeId(),
      uploadedPdf: null,
      errorData: null,
      analysts: [],
      contentTypes: [],
      isPublishing: false,
      isRepublishing: false,
      layouts: [],
      publishedAt: this.data.published_at ? moment.tz(this.data.published_at, 'UTC').tz('Europe/London').format('YYYY-MM-DD hh:mm:00.000Z') : null,
    }
  },

  mounted () {
    this.getlayouts()
    if (this.article.channel_id || this.article.channel) {
      this.getContentTypesAndAnalysts(this.article.channel_id || this.article.channel.id)
    }
  },

  computed: {
    london() {
      return moment.tz(this.publishedAt || new Date(), 'Europe/London').tz('Europe/London').format('YYYY-MM-DD hh:mm a z')
    },
    newYork() {
      return moment.tz(this.publishedAt || new Date(), 'Europe/London').tz('America/New_York').format('YYYY-MM-DD hh:mm a z')
    },
    dubai() {
      return moment.tz(this.publishedAt || new Date(), 'Europe/London').tz('Asia/Dubai').format('YYYY-MM-DD hh:mm a z')
    },
    needsAskBeforePublished () {
      if (!this.article.channel_content_type.id) {
        return false
      }

      const name = this.article.channel_content_type.content_type.name

      return name.toLowerCase().indexOf('alert') === -1 && !this.article.pdf && !this.article.pdf_filename
    },

    canSaveDraft () {
      return this.isRepublish || !this.isDraft || (this.article.channel &&
        this.article.channel_content_type.id
        )
    },

    isContentValid () {
      const {normalLength, validFields} = this.articleCheck();

      return normalLength && validFields;
    },

    isMarketingContentValid() {
      if (this.article.public_title && this.article.public_title.length > 80) {
        return false
      }
      if (this.article.marketing_bullet_1 && this.article.marketing_bullet_1.length > 120) {
        return false
      }
      if (this.article.marketing_bullet_2 && this.article.marketing_bullet_2.length > 120) {
        return false
      }
      if (this.article.marketing_bullet_3 && this.article.marketing_bullet_3.length > 120) {
        return false
      }

      return true
    },

    canPublish () {
      return this.user.permissions.filter(({name}) => name === 'article-drafts.publish').length === 1
    },

    isEditor () {
      return this.user.permissions.filter(({name}) => name === 'articles.view').length === 1
    },
    isProExclusive() {
      return this.article.is_pro_exclusive
    },
    steps () {
      const steps = [
        {
          name: 'type',
          title: 'Choose type',
          component: TypeStep,
          hasErrors: this.errorData ? this.errorData.pdf : false,
          isValid: this.canSaveDraft,
        },
        {
          name: 'content',
          title: 'Article content',
          component: ArticleContentStep,
          hasErrors: this.errorData ?
            (this.errorData.teaser_text || this.errorData.tags || this.errorData.companies || this.errorData.tickers) :
            false,
          isValid: this.isContentValid
        }
      ]

      if (this.isDraft) {
        steps.push({
          name: 'publish',
          title: this.isRepublish ? 'Preview & Republish' : 'Preview & Publish',
          component: PublishStep,
          isValid: this.isContentValid && this.isMarketingContentValid,
        })
      }

      return steps
    },

    isDraft () {
      return !this.article.id || this.isRepublish
    },

  },

  created () {
    this.getRecipients()
  },

  methods: {
    updatePublishedAt () {
      this.applyArticleData({
        published_at: this.publishedAt ? moment.tz(this.publishedAt, 'Europe/London').toISOString() : null,
      })
    },
    cancelSchedule () {
      this.publishedAt = null
      this.updatePublishedAt()
      this.$modal.hide('schedule')
    },
    articleCheck() {
      const {
        title = false,
        // eslint-disable-next-line camelcase
        teaser_text = false,
        tags = [],
        // eslint-disable-next-line camelcase
        bullet_1 = false,
        // eslint-disable-next-line camelcase
        bullet_2 = false,
        // eslint-disable-next-line camelcase
        bullet_3 = false,
      } = this.article

      const normalLength =
        title?.length <= 80
        && bullet_1?.length <= 120
        && bullet_2?.length <= 120
        && bullet_3?.length <= 120

      // eslint-disable-next-line camelcase
      const validFields = this.canSaveDraft
        && (title
          && teaser_text
          && tags.length
          && bullet_1
          && bullet_2
          && bullet_3)

      return {normalLength, validFields}
    },
    async getlayouts () {
      const {data} = await getOnce('articles/layouts')
      this.layouts = data.data
    },
    askToPublish () {
      this.$modal.show('confirm-publish')
    },
    askToRepublish() {
      this.$modal.show('confirm-republish')
    },
    schedule () {
      this.$modal.show('schedule')
    },

    async getRecipients () {
      if (this.article.id) {
        return
      }

      const isProExclusive = this.article.is_pro_exclusive
      let tagIds = this.article.tags.map((tag) => tag.id)
      let contentTypeId
      this.article.analysts.forEach((analyst) => {
        tagIds.push(analyst.tag_id)
      })

      this.article.companies.forEach((company) => {
        // tagIds.push(company.tag.id)
        // tagIds.push(company.country.tag.id)
        // tagIds.push(company.micro_sector.sector.tag.id)
      })

      if (this.article.channel) {
        tagIds.push(this.article.channel.tag_id)
      }

      if (this.article.channel_content_type.id) {
        contentTypeId = this.article.channel_content_type.content_type.id
      }

      tagIds = tagIds.filter((tagId) => !!tagId)
      tagIds = [...new Set(tagIds)]

      const marketCapId = this.article.companies.length === 1 ? this.article.companies[0].market_cap_id : null

      if (tagIds.length > 0 && contentTypeId) {
        try {
          const {data} = await gateway.get(`/crm/follows/total-recipients`, {
            params: {
              tagIds,
              contentTypeId,
              marketCapId,
              isProExclusive: isProExclusive,
              emailOnly: true,
            },
          })
          this.recipients = data.data.totalRecipients
        } catch (e) {
          this.recipients = 0
        }
      } else {
        this.recipients = 0
      }
    },

    goBack () {
      this.navigateToStep(Math.max(0, this.activeStepIndex - 1))
    },

    goNext () {
      this.navigateToStep(Math.min(this.steps.length - 1, this.activeStepIndex + 1))
    },

    navigateToStep (index, save = true) {
      const step = this.steps[this.activeStepIndex]
      const {
        isValid = false,
      } = step
      if (this.activeStepIndex === 1 && index > 1) {
        const companies = this.article.companies
        const companyErrors = []
        for (const i in companies) {
          if (companies[i].security_id && !companies[i].recommendation.recommendation) {
            companyErrors[i] = 'test'
          }
        }

        if (companyErrors.length !== 0) {
          if (this.errorData) {
            this.errorData.companies = companyErrors
          } else {
            this.errorData = {companies: companyErrors}
          }

          return this.scrollIntoView()
        }
      }

      if (save) {
        if ((index < this.activeStepIndex) || (index > this.activeStepIndex && isValid)) {
          // checking if the recommendation is in the title when navigating forward
          if (this.activeStepIndex === 1 && index > this.activeStepIndex) {
            this.titleRecommendationCheck()
          }

          this.saveDraft()
          this.activeStepIndex = index
        } else {
          this.highlightFields = true
          const {normalLength, validFields} = this.articleCheck();
          if(!normalLength){
            this.$notify(errorNotification('Please keep the character limitation.'))
          }
          if(!validFields){
            this.$notify(errorNotification('Please fill out all fields before continuing.'))
          }
          setTimeout(() => this.highlightFields = false, 7000)
        }

        // fixes froala issue where toolbar gets stuck on top of page
        this.scrollIntoView()
      } else {
        this.activeStepIndex = index
      }
    },

    titleRecommendationCheck () {
      if (this.article.companies.length === 1) {
        const {title = ''} = this.article
        const words = title.split(/\W/).filter((word) => word.length).map((word) => word.toLowerCase())
        const {name = ''} = this.article.companies[0].recommendation

        if (name.length && !words.includes(name.toLowerCase())) {
          this.$notify(warningNotification(`Please make sure that your recommendation is present in the title. (${name})`))
        }
      }
    },

    scrollIntoView () {
      const target = document.querySelector('.article-create-main')
      VueScrollTo.scrollTo(target, 500)
    },

    async getContentTypesAndAnalysts(channelId) {
      const analystResponse = await this.$http.get(`/analysts?channel_ids[]=${channelId}&full=1&per_page=1000`)
      this.analysts = analystResponse.data.data
      const contentTypesResponse = await this.$http.get(`/channel-content-types?channel_id=${channelId}`)
      this.contentTypes = contentTypesResponse.data.data
    },

    applyArticleData (articleSlice = {}) {
      if (articleSlice.channel && articleSlice.channel.id != this.article.channel_id) {
        this.getContentTypesAndAnalysts(articleSlice.channel.id)
      }
      this.article = {
        ...this.article,
        ...articleSlice,
      }

      if (this.isDraft) {
        this.delayedSave()
      }
    },

    delayedSave: debounce(function () {
      this.saveDraft()
    }, 5000),

    addTag (tag) {
      const tags = [
        ...this.article.tags,
        tag,
      ]

      this.applyArticleData({tags})
    },

    async saveDraft () {
      if (!this.canSaveDraft || !this.isDraft) {
        return
      }

      try {
        let response
        const {article} = this

        if (article.id) {
          article.article_id = article.id
        }
        if (!article.channel_content_type_id) {
          article.channel_content_type_id = article.channel_content_type?.id
        }

        if (article.article_draft_id) {
          response = await this.$http.post(`/articles/drafts/${article.article_draft_id}/revisions`, article)
        } else {
          response = await this.$http.post('/articles/drafts', article)
          if (!article.id) {
            history.replaceState({}, null, `/articles/draft/${response.data.id}`)
          }
        }
        this.isLoading = false
        this.article.article_draft_id = response.data.article_draft_id || response.data.id
        this.$emit('save-success', response)
        this.$notify(successNotification('Saved draft'))
        this.errorData = null
        this.getRecipients()
      } catch (error) {
        this.handleError(error)
      }
    },

    async saveArticle () {
      if (!this.canPublish) {
        return
      }

      try {
        const {article} = this

        const response = await this.$http.put(`/articles/${article.id}`, article)
        this.isLoading = false
        this.$emit('save-success', response)
        this.$router.go(-1)
        this.$notify(successNotification(`Updated article`))
        this.errorData = null
      } catch (error) {
        this.handleError(error)
      }
    },

    async submitToEditorial () {
      try {
        // eslint-disable-next-line camelcase
        const {article_draft_id, title} = this.article
        await this.saveDraft()
        // eslint-disable-next-line camelcase
        await this.$http.patch(`/articles/drafts/${article_draft_id}/submit`)
        this.$notify(successNotification(`<em>&quot;${title}&quot;</em> has been submitted for approval.`))
        this.$router.push('/articles')
      } catch (error) {
        this.handleError(error)
      }
    },

    async publishArticle () {
      if (this.isPublishing) {
        return
      }

      this.isPublishing = true
      // this.$notify({...infoNotification('Publishing. Please do not refresh or navigate away.'), duration: -1, closeOnClick: false})
      this.$modal.hide('schedule')
      try {
        await this.saveDraft()
        const {data} = await this.$http.patch('/articles/drafts/' + this.article.article_draft_id + '/publish')
        this.$router.push(`/articles/${data.id}`)
      } catch (error) {
        this.handleError(error)
      }
      this.isPublishing = false
      // this.$notify({
      //   clean: true
      // })
    },

    async deleteArticle () {
      const {article_draft_id: draftId, id, title} = this.article
      this.$http.delete(draftId ? `/articles/drafts/${draftId}` : `/articles/${id}`)

      this.$notify(successNotification(`${title || 'The article'} has been deleted.`))
      this.$router.replace({name: 'articles', hash: '#fresh'})
    },

    async sendTest () {
      const users = [this.$login.getUser()]
      const url = `alerts/test/${this.article.article_draft_id}/article-draft`

      await gateway.post(url, {users})
      this.$notify(successNotification('Email sent!'))
    },

    async republishAndSendAlert() {
      if(!this.canPublish) {
        return;
      }
      if(this.isPublishing) {
        return;
      }

      this.isLoading = true
      this.isPublishing = true;
      const { article } = this
      try {
        await this.saveDraft()
        await this.$http.put(`/articles/${article.id}/republish`, article)
        this.$router.push({name: 'articles.view', params: {articleId: article.id}})
        this.errorData = null
      } catch (error) {
        this.handleError(error)
      }
      this.isLoading = false
      this.isPublishing = false;

    },

    handleError (errorResponse) {
      this.isLoading = false
      this.errorData = {}
      const error = errorResponse.response.data
      const {errors = {}} = error
      Object.keys(errors).forEach((key) => {
        this.errorData[key] = errors[key][0]
      })
      for (const index in this.steps) {
        if (this.steps[index].hasErrors) {
          this.navigateToStep(parseInt(index, 10), false)
          this.$nextTick(() => {
            const target = document.querySelector('.is-invalid')
            this.$scrollTo(target)
          })
          return
        }
      }
    },
  },
}
</script>

<style lang="scss">
.loading {
  opacity: .7;
}
.article-create {
  &-main {
    margin-top: 2rem;
  }

  &-navigation {
    margin-top: 2rem;
    padding: 1.5rem 0;
    border-top: 1px solid $border-color;


  }

  &__loader {
    height: calc(100vh - 200px);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>

